import React, { useContext, useEffect } from "react";
import { initReactI18next } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";
import i18n from "i18next";
import DeletedUser from "@/App/Pages/Admin/DeletedUser.jsx";
import GetData from "@/App/Pages/Admin/GetData.jsx";
import AdminLogin from "@/App/Pages/Admin/Login.jsx";
import ResumeData from "@/App/Pages/Admin/ResumeData.jsx";
import Reviews from "@/App/Pages/Admin/Reviews.jsx";
import User from "@/App/Pages/Admin/User.jsx";
import TemplateImagePreloader from "@/Components/Common/TemplateImagePreloader";
import { UserContext } from "@/Context/UserContext";
import { logger } from "@/Services/Logger/Index";
import { isAdmin, setSessionStorageItem } from "@/Utils/Index";
import { About } from "../Pages/About/About.jsx";
import Account from "../Pages/Account/Account.jsx";
import Dashboard from "../Pages/Dashboard/Dashboard.jsx";
import { Home } from "../Pages/Home/Home.jsx";
import { ForgotPassword } from "../Pages/InterviewsByAI/Login/ForgotPassword.jsx";
import { ResetPassword } from "../Pages/InterviewsByAI/Login/ResetPassword.jsx";
import { VerifyToken } from "../Pages/InterviewsByAI/SignUp/VerifyToken.jsx";
import PrivacyPolicy from "../Pages/PrivacyPolicy/PrivacyPolicy.jsx";
import ResumeAnalysis from "../Pages/ResumeAnalysis/ResumeAnalysis.jsx";
import ResumeChoice from "../Pages/ResumeChoice/ResumeChoice.jsx";
import FiveSecondScore from "../Pages/ResumeScore/FiveSecondScore.jsx";
import NaukriJobInput from "../Pages/NaukriJobInput/NaukriJobInput.jsx";
import ResumeSetup from "../Pages/ResumeSetup/ResumeSetup.jsx";
import ResumeUpload from "../Pages/ResumeUpload/ResumeUpload.jsx";
import ModalBasedResume from "../Pages/ResumeView/ModalBasedResume.jsx";
import ReactPDF from "../Pages/ResumeView/ReactPDFResume.jsx";
import ResumeEditingChoice from "../Pages/ResumeView/ResumeEditingChoice.jsx";
import ProfilePhotoUpload from "../Components/Common/ProfilePhotoUpload.jsx";
import ResumePreview from "../Pages/ResumeView/ResumePreview.jsx";
import ResumeView from "../Pages/ResumeView/ResumeView.jsx";
import TermsAndConditions from "../Pages/TermsAndConditions/TermsAndConditions.jsx";
import ThankYou from "../Pages/ThankYou/ThankYou.jsx";
import PaymentResult from "../Pages/Billing/PaymentResult.jsx";
import { Access } from "./../Pages/InterviewsByAI/Access/Access.jsx";
import { Login } from "./../Pages/InterviewsByAI/Login/Login.jsx";
import { SignUp } from "./../Pages/InterviewsByAI/SignUp/SignUp.jsx";
import NotFound from "./../Pages/NotFound/NotFound.jsx";
import Organization from "./Pages/Admin/Organization.jsx";
import {
  AppLayoutRoute,
  AppRoute,
  GeneralRoute,
  PrivateAdminRoute,
  ProtectedResumeRoute,
  PublicAdminRoute,
} from "./ProtectedRoutes.jsx";

const PublicRouteWrapper = () => {
  const { userProfileData } = useContext(UserContext);

  useEffect(() => {
    const initializeI18n = async () => {
      try {
        await i18n.use(initReactI18next).init();

        let languageToLoad = "en"; // Default to English
        if (!isAdmin() && userProfileData?.appLanguage) {
          languageToLoad = userProfileData.appLanguage;
        }
        const languageResource = await import(`@/LanguageJS/${languageToLoad}.js`);
        i18n.addResourceBundle(languageToLoad, "translation", languageResource.default);
        await i18n.changeLanguage(languageToLoad);
        setSessionStorageItem("appLanguage", languageToLoad);
      } catch (error) {
        logger.error(error);
      }
    };

    initializeI18n();
  }, [userProfileData?.appLanguage, i18n]);

  return (
    <>
      <TemplateImagePreloader />
      <Routes>
        {/* PUBLIC ROUTES - Login/Signup accessible without auth */}
        <Route element={<GeneralRoute />}>
          {/* Redirect /app to dashboard */}
          <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsAndConditions />} />
          <Route path="/access" element={<Access />} />
          <Route path="/verify" element={<VerifyToken />} />
          <Route path="/app/resume-score" element={<FiveSecondScore />} />
          {/* Authentication routes - Public */}
          <Route path="/app/login" element={<Login />} />
          <Route path="/app/signup" element={<SignUp />} />
          <Route exact path="/app/forgot-password" element={<ForgotPassword />} />
          {/* Resume routes - Public (auth required only at download) */}
          <Route path="/app/resume-choice" element={<ResumeChoice />} />
          <Route path="/app/naukri-job-input" element={<NaukriJobInput />} />
          <Route path="/app/resume-setup" element={<ResumeSetup />} />
          <Route path="/app/resume-upload" element={<ResumeUpload />} />
          <Route path="/app/resume-score/:resumeId" element={<ResumeAnalysis />} />
          <Route path="/app/resume/:resumeId/chat" element={<ResumeView />} />
          <Route path="/app/resume/:resumeId/choice" element={<ResumeEditingChoice />} />
          <Route path="/app/resume/:resumeId" element={<ModalBasedResume />} />
        </Route>

        {/* PROTECTED RESUME ROUTES - Require authentication */}
        <Route element={<ProtectedResumeRoute />}>
          <Route path="/app/resume/:resumeId/preview" element={<ResumePreview />} />
          <Route path="/app/resume/:resumeId/edit-photo" element={<ProfilePhotoUpload renderMode="page" />} />
          <Route path="/app/resume/:resumeId/react-pdf" element={<ReactPDF />} />
          <Route path="/app/resume/:resumeId/thank-you" element={<ThankYou />} />
        </Route>

        <Route element={<AppRoute type="public" />}>
          <Route exact path="/app/reset" element={<ResetPassword />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route element={<AppRoute type="private" />}>
          <Route exact path="/home" element={<Home />} />
          <Route exact path="/about" element={<About />} />

          <Route path="*" element={<NotFound />} />
        </Route>
        <Route element={<AppLayoutRoute />}>
          <Route exact path="/app/dashboard" element={<Dashboard />} />
        </Route>
        <Route element={<GeneralRoute />}>
          <Route exact path="/app/account" element={<Account />} />
          <Route exact path="/app/payment-result" element={<PaymentResult />} />
        </Route>
        <Route element={<PublicAdminRoute />}>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/app/admin/login" element={<AdminLogin />} />
        </Route>
        <Route element={<PrivateAdminRoute />}>
          <Route path="/app/admin/user" element={<User />} />
          <Route path="/app/admin/organization" element={<Organization />} />
          <Route path="/app/admin/deleteuser" element={<DeletedUser />} />
          <Route path="/app/admin/getdata" element={<GetData />} />
          <Route path="/app/admin/resumedata" element={<ResumeData />} />
          <Route path="/app/admin/reviews" element={<Reviews />} />
        </Route>
      </Routes>
    </>
  );
};

export default PublicRouteWrapper;
